import { ChevronRight } from "lucide-react";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface Toggle {
  id: string;
  label: string;
  path?: string;
}

interface DetailHeaderProps {
  breadcrumb: BreadcrumbItem[];
  toggleOptions?: Toggle[];
  activeToggle?: string;
  onToggleChange?: (toggleId: string) => void;
  actions?: React.ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
}

export function DetailHeader({ 
  breadcrumb, 
  toggleOptions, 
  activeToggle, 
  onToggleChange, 
  actions, 
  searchValue, 
  onSearchChange, 
  searchPlaceholder 
}: DetailHeaderProps) {
  return (
    <div className="bg-background border-b border-border h-[65px]">
      <div className="px-6 h-full flex items-center">
        {/* Left: Breadcrumb */}
        <div className="flex-1">
          <nav className="flex items-center space-x-2 text-xs">
            {breadcrumb.map((item, index) => (
              <div key={`breadcrumb-${item.label}-${index}`} className="flex items-center">
                {index > 0 && (
                  <ChevronRight className="w-4 h-4 text-muted-foreground mx-1" />
                )}
                {item.href ? (
                  <a
                    href={item.href}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {item.label}
                  </a>
                ) : (
                  <span className="text-foreground font-medium">{item.label}</span>
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* Center: Toggle Controls */}
        <div className="flex items-center justify-center">
          {toggleOptions && toggleOptions.length > 0 && (
            <div className="flex rounded-lg bg-muted p-1">
              {toggleOptions.map((option) => (
                <button
                  key={option.id}
                  onClick={() => onToggleChange?.(option.id)}
                  className={`px-3 py-1.5 cursor-pointer text-xs font-medium rounded-md transition-colors ${
                    activeToggle === option.id
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: Search and Actions */}
        <div className="flex-1 flex items-center justify-end gap-3">
          {onSearchChange && (
            <div className="relative">
              <input
                type="text"
                value={searchValue || ""}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder || "Search..."}
                className="w-64 px-3 py-2.5 text-xs border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </div>
          )}
          {actions && (
            <div className="flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
